import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { workAuthService } from '@/services/workAuthService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, Upload, AlertCircle, FileText } from 'lucide-react';

interface UploadData {
  renewalCycle: any;
  workAuthorization: any;
  employee: any;
}

const WorkAuthUploadPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [uploadData, setUploadData] = useState<UploadData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Upload form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>('I9_FORM');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Invalid upload link');
      setValidating(false);
      return;
    }

    try {
      const data = await workAuthService.validateUploadToken(token);
      setUploadData(data);
      setValidating(false);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        'This upload link is invalid or has expired. Please contact HR for assistance.'
      );
      setValidating(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !token) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      await workAuthService.uploadDocumentsByToken(token, documentType, selectedFile);
      setSuccess(true);
    } catch (err: any) {
      setUploadError(
        err.response?.data?.message ||
        'Failed to upload document. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-muted-foreground">Validating upload link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <CardTitle>Invalid Upload Link</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="mt-6">
              <p className="text-sm text-muted-foreground">
                If you believe this is an error, please contact your HR department for assistance.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <CardTitle>Documents Uploaded Successfully</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle>Success!</AlertTitle>
              <AlertDescription>
                Your work authorization documents have been submitted to HR for review.
              </AlertDescription>
            </Alert>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• HR will review your submitted documents</li>
                <li>• You will receive an email notification with the review results</li>
                <li>• If additional information is needed, HR will contact you</li>
              </ul>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              You can safely close this window.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Work Authorization Document Upload</CardTitle>
            <CardDescription>
              Upload required work authorization documents for review
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Employee Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-3">Employee Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <p className="font-medium">
                    {uploadData?.employee.firstName} {uploadData?.employee.lastName}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Employee ID:</span>
                  <p className="font-medium">{uploadData?.employee.employeeId}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Current Expiration:</span>
                  <p className="font-medium">
                    {new Date(uploadData?.workAuthorization.expirationDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Document Type:</span>
                  <p className="font-medium">{uploadData?.workAuthorization.documentType}</p>
                </div>
              </div>
            </div>

            {/* Upload Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="documentType">Document Type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="I9_FORM">I-9 Form</SelectItem>
                    <SelectItem value="WORK_PERMIT">Work Permit</SelectItem>
                    <SelectItem value="VISA">Visa</SelectItem>
                    <SelectItem value="GREEN_CARD">Green Card</SelectItem>
                    <SelectItem value="EMPLOYMENT_AUTHORIZATION">Employment Authorization Document</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="document">Upload Document</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="document"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </div>
                {selectedFile && (
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    <span>{selectedFile.name}</span>
                    <span className="text-gray-400">
                      ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Accepted formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
                </p>
              </div>

              {uploadError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{uploadError}</AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="w-full"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>

            {/* Information */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Important Information</h3>
              <ul className="text-sm text-foreground space-y-1">
                <li>• Ensure all documents are clear and legible</li>
                <li>• Upload only valid, unexpired documents</li>
                <li>• HR will review your submission within 2-3 business days</li>
                <li>• You will receive an email notification once reviewed</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorkAuthUploadPage;